import { ConfirmDialog } from '@/common/dialog/ConfirmDialog';
import { KrDialog } from '@/common/dialog/Dialog';
import { useDialog } from '@/common/dialog/DialogContext';
import { faPlus, faSave, faTimes } from '@awesome.me/kit-b4fd369ea4/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button } from '@mui/material';
import { createContext, useContext, useRef, useState } from 'react';
import { EntityFormMode } from '../enumerations/EntityFormMode';
import { EntityForm } from './EntityForm';

// Lets a form field inside the dialog contribute action buttons rendered next to Cancel/Save.
// Outside a dialog the setter is a no-op, so fields can call it unconditionally.
const EntityFormDialogActionsContext = createContext({ setExtraButtons: () => {} });
export const useEntityFormDialogActions = () => useContext(EntityFormDialogActionsContext);

/**
 * Modal add/edit for a leaf Entity, keeping the page behind it in place.
 *
 * Resolves (via closeDialog) with the saved Entity, or with an array of everything saved when
 * "Save & add another" was used. Resolves undefined on cancel.
 *
 * renderForm lets callers supply an Entity-specific form (e.g. ProgProdForm) instead of the bare EntityForm;
 * it receives the props that must be forwarded to the inner EntityForm.
 */
export const EntityFormDialog = ({
  closeDialog,
  title,
  subtitle,
  module,
  entity,
  mode = EntityFormMode.ADD,
  entityId,
  initialValues,
  allowAddAnother = false,
  getNextInitialValues,
  maxWidth = 'sm',
  // Optional content rendered above the form (e.g. a parent context strip)
  header,
  renderForm,
}) => {
  const { openDialog } = useDialog();
  const formRef = useRef();

  // Remount the form after "Save & add another"
  const [formKey, setFormKey] = useState(0);
  const [currentInitialValues, setCurrentInitialValues] = useState(initialValues);
  const [extraButtons, setExtraButtons] = useState(null);
  const savedRef = useRef([]);
  const addAnotherRef = useRef(false);

  const finish = (result) => {
    const allSaved = savedRef.current;
    if (allSaved.length === 0) {
      closeDialog(result);
    } else {
      closeDialog(result ? [...allSaved, ...[].concat(result)] : allSaved);
    }
  };

  const onSaved = (savedEntityObj) => {
    if (addAnotherRef.current) {
      addAnotherRef.current = false;
      // A single submission may have produced several Entities
      savedRef.current = [...savedRef.current, ...[].concat(savedEntityObj)];
      const savedEntityObjs = [].concat(savedEntityObj);
      const lastSavedEntityObj = savedEntityObjs[savedEntityObjs.length - 1];
      setCurrentInitialValues((values) => getNextInitialValues?.(lastSavedEntityObj, values) ?? values);
      setFormKey((key) => key + 1);
      return;
    }
    finish(savedEntityObj);
  };

  const cancel = () => {
    if (!formRef.current?.isDirty()) {
      finish();
      return;
    }
    openDialog(ConfirmDialog, {
      title: 'Discard changes?',
      message: 'You have unsaved changes in this form.',
      confirmLabel: 'Discard',
      confirmColor: 'error',
    }).then((confirmed) => {
      if (confirmed) finish();
    });
  };

  const save = () => {
    addAnotherRef.current = false;
    formRef.current?.save();
  };

  const saveAndAddAnother = () => {
    addAnotherRef.current = true;
    formRef.current?.save();
  };

  const formProps = {
    ref: formRef,
    module,
    entity,
    mode,
    entityId,
    initialValues: currentInitialValues,
    onSaved,
    onCancel: cancel,
    hideButtons: true,
    compact: true,
  };

  const buttons = [
    <Button key="cancel" color="secondary" variant="outlined" onClick={cancel}>
      <Box component="span" sx={{ mr: 1.3 }}>
        <FontAwesomeIcon icon={faTimes} />
      </Box>
      Cancel
    </Button>,
  ];

  if (allowAddAnother && mode === EntityFormMode.ADD) {
    buttons.push(
      <Button key="saveAddAnother" color="primary" variant="outlined" onClick={saveAndAddAnother}>
        <Box component="span" sx={{ mr: 1.3 }}>
          <FontAwesomeIcon icon={faPlus} />
        </Box>
        Save & add another
      </Button>,
    );
  }

  if (extraButtons) {
    buttons.push(<Box key="extra">{extraButtons}</Box>);
  }

  buttons.push(
    <Button key="save" color="primary" variant="contained" onClick={save}>
      <Box component="span" sx={{ mr: 1.3 }}>
        <FontAwesomeIcon icon={faSave} />
      </Box>
      Save
    </Button>,
  );

  return (
    <EntityFormDialogActionsContext.Provider value={{ setExtraButtons }}>
      <KrDialog title={title} subtitle={subtitle} maxWidth={maxWidth} closeDialog={cancel} buttons={buttons}>
        {header}
        <Box key={formKey}>{renderForm ? renderForm(formProps) : <EntityForm {...formProps} />}</Box>
      </KrDialog>
    </EntityFormDialogActionsContext.Provider>
  );
};
